import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { HiOutlineX } from 'react-icons/hi';
import { MdEdit, MdDelete } from 'react-icons/md';
import { BrandNote, submitBrandNote, updateBrandNote, deleteBrandNote } from '../../services/brandService';
import { StoreNote, fetchStoreNotes, submitStoreNote, updateStoreNote, deleteStoreNote } from '../../services/brandSitesService';
import { useAuthStore } from '../../stores';
import ThemeLoader from '../ThemeLoader/ThemeLoader';
import './BrandNotesDialog.css';

interface Comment {
  id: string;
  text: string;
  timestamp: number;
  brandName: string;
  userName: string;
}

interface BrandNotesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  brandName: string;
  notes?: BrandNote[] | StoreNote[] | null;
  isLoading?: boolean;
  onNoteAdded?: () => void;
  poiId?: string; // For store notes
  isStoreNote?: boolean; // Flag to indicate if this is store note
}

// Convert database notes to Comment format
const convertNotesToComments = (notes: (BrandNote | StoreNote)[], brandName: string): Comment[] => {
  return notes.map(note => ({
    id: note.id,
    text: note.comment,
    timestamp: note.created_at ? new Date(note.created_at).getTime() : Date.now(),
    brandName,
    userName: (note.user && note.user.name) ? note.user.name : 'Unknown User'
  }));
};

export default function BrandNotesDialog({
  isOpen,
  onClose,
  brandName,
  notes = [],
  isLoading = false,
  onNoteAdded,
  poiId,
  isStoreNote = false,
}: BrandNotesDialogProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [isLoadingNotes, setIsLoadingNotes] = useState(isLoading);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const wasOpenRef = useRef(false);
  const hasChangesRef = useRef(false);

  // Load comments when dialog opens or when notes change
  useEffect(() => {
    setIsLoadingNotes(isLoading);
  }, [isLoading]);

  // Load notes when dialog first opens (for store notes, fetch fresh from API)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Dialog just opened
      hasChangesRef.current = false;

      if (isStoreNote && poiId) {
        // For store notes, always fetch fresh from API to avoid stale cache
        setIsLoadingNotes(true);
        fetchStoreNotes(poiId).then(freshNotes => {
          if (freshNotes && freshNotes.length > 0) {
            setComments(convertNotesToComments(freshNotes, brandName));
          } else {
            setComments([]);
          }
          setIsLoadingNotes(false);
          // Sync fresh notes back to parent so badge counts stay accurate
          onNoteAdded?.();
        }).catch(() => {
          // Fallback to prop if fetch fails
          if (notes && notes.length > 0) {
            setComments(convertNotesToComments(notes, brandName));
          } else {
            setComments([]);
          }
          setIsLoadingNotes(false);
        });
      }
    }
    if (!isOpen && wasOpenRef.current && hasChangesRef.current) {
      // Dialog just closed and had changes — notify parent to refresh counts
      onNoteAdded?.();
      hasChangesRef.current = false;
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, brandName, notes, onNoteAdded, isStoreNote, poiId]);

  // For brand notes, sync from notes prop whenever it changes (handles async fetch)
  useEffect(() => {
    if (isOpen && !isStoreNote && !hasChangesRef.current) {
      if (notes && notes.length > 0) {
        setComments(convertNotesToComments(notes, brandName));
      } else if (!isLoading) {
        setComments([]);
      }
    }
  }, [isOpen, isStoreNote, notes, brandName, isLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setIsSubmitting(true);

    try {
      // Get userId from authenticated user
      const authUser = useAuthStore.getState().user;
      const userId = (authUser as any)?.id;

      if (!userId) {
        console.error('User not authenticated');
        setNewComment('');
        setIsSubmitting(false);
        return;
      }

      // Submit to appropriate API based on note type
      let result;
      if (isStoreNote && poiId) {
        result = await submitStoreNote(poiId, userId, newComment.trim());
      } else {
        result = await submitBrandNote(brandName, userId, newComment.trim());
      }

      if (result) {
        // Add the new note directly to comments state using functional update to avoid stale closure
        const newCommentData: Comment = {
          id: result.id,
          text: result.comment,
          timestamp: result.created_at ? new Date(result.created_at).getTime() : Date.now(),
          brandName,
          userName: result.user?.name || (authUser as any)?.name || 'Unknown User'
        };
        setComments(prev => [newCommentData, ...prev]);
        setNewComment('');
        hasChangesRef.current = true;
        // Immediately notify parent to update badge count
        onNoteAdded?.();
      } else {
        // Submit returned null — try refetching notes from server as fallback
        console.error('Submit returned null, refetching notes from server');
        if (isStoreNote && poiId) {
          const freshNotes = await fetchStoreNotes(poiId);
          if (freshNotes && freshNotes.length > 0) {
            setComments(convertNotesToComments(freshNotes, brandName));
          }
        }
        setNewComment('');
        hasChangesRef.current = true;
      }
    } catch (error) {
      console.error('Error submitting note:', error);
      // Even on error, try refetching in case the mutation did succeed
      if (isStoreNote && poiId) {
        try {
          const freshNotes = await fetchStoreNotes(poiId);
          if (freshNotes && freshNotes.length > 0) {
            setComments(convertNotesToComments(freshNotes, brandName));
          }
        } catch (_) { /* ignore refetch error */ }
      }
      setNewComment('');
      hasChangesRef.current = true;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      // Delete from API using appropriate function
      if (isStoreNote) {
        await deleteStoreNote(commentId);
      } else {
        await deleteBrandNote(commentId);
      }
      // Remove from local state
      setComments(prev => prev.filter(c => c.id !== commentId));
      hasChangesRef.current = true;
      // Immediately notify parent to update badge count
      onNoteAdded?.();
    } catch (error) {
      console.error('Error deleting note:', error);
      // Still remove from local state
      setComments(prev => prev.filter(c => c.id !== commentId));
      hasChangesRef.current = true;
      onNoteAdded?.();
    }
  };

  const handleEdit = (commentId: string, currentText: string) => {
    setEditingCommentId(commentId);
    setEditText(currentText);
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editText.trim()) return;

    try {
      // Update via API using appropriate function
      const updated = isStoreNote ? await updateStoreNote(commentId, editText.trim()) : await updateBrandNote(commentId, editText.trim());

      if (updated) {
        // Update local state with response from API
        setComments(prev => prev.map(c =>
          c.id === commentId
            ? {
                ...c,
                text: updated.comment,
                timestamp: updated.updated_at ? new Date(updated.updated_at).getTime() : c.timestamp
              }
            : c
        ));
      }

      setEditingCommentId(null);
      setEditText('');
      hasChangesRef.current = true;
    } catch (error) {
      console.error('Error updating note:', error);
      setEditingCommentId(null);
      setEditText('');
      hasChangesRef.current = true;
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditText('');
  };

  const formatFullDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <div className="notes-dialog-overlay" onClick={handleOverlayClick}>
      <div className="notes-dialog">
        <div className="notes-dialog-header">
          <h2>Notes for {brandName}</h2>
          <button onClick={onClose} className="notes-close-btn">
            <HiOutlineX />
          </button>
        </div>

        <div className="notes-dialog-description">
          Add your notes here. Only you and the development team can view these notes.
        </div>

        <div className="notes-dialog-body">
          {/* Comment form */}
          <form onSubmit={handleSubmit} className="notes-form">
            <textarea
              placeholder="Type your note here..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="notes-textarea"
              disabled={isLoadingNotes}
            />
            <div className="notes-form-footer">
              <button
                type="submit"
                disabled={!newComment.trim() || isLoadingNotes || isSubmitting}
                className="notes-submit-btn"
              >
                {isSubmitting ? 'Submitting...' : 'Add Note'}
              </button>
            </div>
          </form>

          {/* Loading indicator */}
          {isLoadingNotes && <ThemeLoader text="Loading notes" />}

          {/* Comments list */}
          {!isLoadingNotes && comments.length > 0 && (
            <div className="notes-comments-list">
              <div className="notes-comments-header">
                Notes ({comments.length})
              </div>
              {comments.map((comment) => (
                <div key={comment.id} className="notes-comment-card">
                  {editingCommentId === comment.id ? (
                    <div className="notes-edit-mode">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="notes-textarea"
                      />
                      <div className="notes-edit-buttons">
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="notes-btn-cancel"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveEdit(comment.id)}
                          className="notes-btn-save"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="notes-comment-header">
                        <div className="notes-user-info">
                          <div className="notes-user-avatar">
                            {(comment.userName || 'U').charAt(0).toUpperCase()}
                          </div>
                          <span className="notes-user-email">
                            {comment.userName || 'Unknown User'}
                          </span>
                        </div>
                        <div className="notes-comment-actions">
                          <button
                            type="button"
                            onClick={() =>
                              handleEdit(comment.id, comment.text)
                            }
                            className="notes-action-btn notes-edit-btn"
                            title="Edit"
                          >
                            <MdEdit />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(comment.id)}
                            className="notes-action-btn notes-delete-btn"
                            title="Delete"
                          >
                            <MdDelete />
                          </button>
                        </div>
                      </div>

                      <p className="notes-comment-text">{comment.text}</p>

                      <div className="notes-comment-timestamp">
                        {formatFullDate(comment.timestamp)}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isLoadingNotes && comments.length === 0 && (
            <div className="notes-empty-state">
              No notes added yet. Share your thoughts above.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
